'use client';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import { ResponsiveImage } from '../ResponsiveImage';
import { useReporter } from '../../reporter';
import {
  useDotButton,
  usePrevNextButtons,
  useThumbnailButtons,
} from './functions';
import {
  userClicksPrevArrow,
  userClicksNextArrow,
  userClicksDotButton,
  userClicksThumbnail,
} from './events';
import {
  StyledCarousel,
  SlideContainer,
  SlideViewport,
  SlideContent,
  DotContainer,
  PrevButton,
  NextButton,
  DotButton,
  Slide,
  BottomContainer,
  ImageDot,
  ThumbViewport,
  ThumbContainer,
  ThumbSlide,
  ThumbContent,
} from './styles.js';

export const Carousel = ({
  paddingTop = 0,
  paddingBottom = 4,
  verticalPadding = 9,
  children,
  bottomElement,
  dotPadding,
  dotVariant = 'darkerFur',
  buttonPosition = '50',
  overlappingArrows = true,
  overlappingDots = false,
  showArrows = true,
  hideArrowMobile = false,
  showDots = true,
  itemsToShow,
  dotImages,
  onArrowClick,
  onDotClick,
  dotColor = 'text.secondary',
  arrowBgColor = 'text.primary',
  thumbBorderColor = 'secondary.egg',
  thumbSize = 100,
  thumbAlign = 'center',
  slideAlign = 'center',
  showArrowBorder = true,
  arrowButtonPadding = '10px',
  slideFullWidth = false,
  withThumbnails = false,
}) => {
  //* Main Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel();

  //* Thumbnails Carousel
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const { onThumbClick, selectedIndex: thumbSelectedIndex } =
    useThumbnailButtons(emblaApi, emblaThumbsApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  //* Event tracking here
  const reporter = useReporter();
  const onArrowClickWithTracking = (isNext = false) => {
    if (isNext) {
      reporter.tag(userClicksNextArrow());
    } else {
      reporter.tag(userClicksPrevArrow());
    }
    onArrowClick?.();
  };
  const onDotClickWithTracking = dotIndex => {
    reporter.tag(userClicksDotButton(dotIndex));
    onDotClick?.();
  };
  const onThumbClickWithTracking = thumbIndex => {
    reporter.tag(userClicksThumbnail(thumbIndex));
    onThumbClick(thumbIndex);
  };

  return (
    <StyledCarousel
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      verticalPadding={verticalPadding}
    >
      <SlideViewport ref={emblaRef}>
        <SlideContainer>
          {children.map((child, index) => (
            <Slide
              key={index}
              itemsToShow={withThumbnails ? 1 : itemsToShow}
              fullWidth={slideFullWidth}
              $removePadding={withThumbnails}
            >
              <SlideContent fullWidth={slideFullWidth} $slideAlign={slideAlign}>
                {child}
              </SlideContent>
            </Slide>
          ))}
        </SlideContainer>
      </SlideViewport>
      {showArrows && (
        <>
          <PrevButton
            onClick={() => {
              onPrevButtonClick();
              onArrowClickWithTracking();
            }}
            hideArrowMobile={hideArrowMobile}
            showArrowBorder={showArrowBorder}
            arrowButtonPadding={arrowButtonPadding}
            disabled={prevBtnDisabled}
            buttonPosition={buttonPosition}
            overlappingArrows={overlappingArrows}
            $arrowBgColor={arrowBgColor}
          />
          <NextButton
            onClick={() => {
              onNextButtonClick();
              onArrowClickWithTracking(true);
            }}
            hideArrowMobile={hideArrowMobile}
            showArrowBorder={showArrowBorder}
            arrowButtonPadding={arrowButtonPadding}
            disabled={nextBtnDisabled}
            buttonPosition={buttonPosition}
            overlappingArrows={overlappingArrows}
            $arrowBgColor={arrowBgColor}
          />
        </>
      )}
      {withThumbnails && (
        <ThumbViewport ref={emblaThumbsRef}>
          <ThumbContainer $thumbAlign={thumbAlign}>
            {children.map((child, index) => (
              <ThumbSlide
                key={index}
                itemsToShow={children.length}
                fullWidth={true}
                $removePadding={true}
                style={{ cursor: 'pointer' }}
                $active={index === thumbSelectedIndex}
                $thumbBorderColor={thumbBorderColor}
                $thumbSize={thumbSize}
                onClick={() => onThumbClickWithTracking(index)}
              >
                <ThumbContent>{child}</ThumbContent>
              </ThumbSlide>
            ))}
          </ThumbContainer>
        </ThumbViewport>
      )}
      <BottomContainer
        dotPadding={dotPadding}
        overlappingDots={overlappingDots}
      >
        {bottomElement}
        {showDots && scrollSnaps.length > 1 && (
          <DotContainer>
            {dotImages
              ? scrollSnaps.map((_, index) => (
                  <ImageDot
                    key={index}
                    onClick={() => {
                      onDotClickWithTracking(index);
                      onDotButtonClick(index);
                    }}
                    active={index === selectedIndex}
                  >
                    <ResponsiveImage {...dotImages[index]} />
                  </ImageDot>
                ))
              : scrollSnaps.map((_, index) => (
                  <DotButton
                    $dotColor={dotColor}
                    key={index}
                    onClick={() => {
                      onDotClickWithTracking(index);
                      onDotButtonClick(index);
                    }}
                    active={index === selectedIndex}
                    variant={dotVariant}
                  />
                ))}
          </DotContainer>
        )}
      </BottomContainer>
    </StyledCarousel>
  );
};

Carousel.displayName = 'Carousel';

Carousel.propTypes = {
  paddingTop: PropTypes.number,
  paddingBottom: PropTypes.number,
  verticalPadding: PropTypes.number,
  children: PropTypes.node.isRequired,
  buttonPosition: PropTypes.oneOf(['15', '17', '30', '40', '45', '50']),
  dotVariant: PropTypes.string,
  overlappingArrows: PropTypes.bool,
  hideArrowsMobile: PropTypes.bool,
  showArrowBorder: PropTypes.bool,
  arrowButtonPadding: PropTypes.string,
  slideFullWidth: PropTypes.bool,
  overlappingDots: PropTypes.bool,
  bottomElement: PropTypes.node,
  dotPadding: PropTypes.number,
  itemsToShow: PropTypes.number,
  dotImages: PropTypes.array,
  onArrowClick: PropTypes.func,
  onDotClick: PropTypes.func,
  withThumbnails: PropTypes.bool,
  thumbBorderColor: PropTypes.string,
  thumbSize: PropTypes.number,
  thumbAlign: PropTypes.oneOf(['start', 'center', 'end']),
  slideAlign: PropTypes.oneOf(['start', 'center', 'end']),
};
